import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3, Download, RefreshCw, TrendingUp, Users } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { AdminPageHeader, StateBlock } from "./components/AdminUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/commerciale`;
const PERIODS = [
  { value: "30", label: "Ultimi 30 giorni" },
  { value: "90", label: "Ultimi 90 giorni" },
  { value: "365", label: "Ultimi 12 mesi" },
];

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function label(value) {
  return value == null || value === "" ? "Non classificato" : String(value);
}

function conversionRate(conversion) {
  const rate = Number(conversion?.rate);
  if (Number.isFinite(rate)) return rate <= 1 ? rate * 100 : rate;
  const total = number(conversion?.total);
  return total ? (number(conversion?.clienti) / total) * 100 : 0;
}

function shortLabel(value, max = 18) {
  const text = label(value);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * Report commerciale (lead strutture R&CO./Affidya) con export CSV.
 * Standalone usa il filtro "ultimi N giorni"; dentro "Dashboard e report"
 * (`filters`) usa periodo e operatore comuni, con gli stessi endpoint.
 */
export default function ReportCommercialeModule({ embedded = false, filters = null }) {
  const [days, setDays] = useState("30");
  const [error, setError] = useState("");
  const controlled = Boolean(filters);
  const filterQuery = controlled
    ? new URLSearchParams(
        Object.entries({ date_from: filters.dateFrom, date_to: filters.dateTo, commerciale: filters.operatore }).filter(([, v]) => v)
      ).toString()
    : `days=${encodeURIComponent(days)}`;
  const [data, setData] = useState({
    days: 30,
    pipeline: [],
    conversion: {},
    by_commerciale: [],
    andamento: [],
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [commercialeFilter, setCommercialeFilter] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(`${API}/stats/report?${filterQuery}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.detail || "Impossibile caricare il report commerciale");
      setData({
        days: json.days || Number(days),
        pipeline: Array.isArray(json.pipeline) ? json.pipeline : [],
        conversion: json.conversion || {},
        by_commerciale: Array.isArray(json.by_commerciale) ? json.by_commerciale : [],
        andamento: Array.isArray(json.andamento) ? json.andamento : [],
      });
    } catch (err) {
      setError(err.message || "Errore nel caricamento del report commerciale");
      toast.error(err.message || "Errore nel caricamento del report commerciale");
    } finally {
      setLoading(false);
    }
  }, [days, filterQuery]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(`${API}/stats/report/export?${filterQuery}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.detail || "Impossibile esportare il report");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition") || "";
      const filename = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1] || `report-commerciale-${days}g.csv`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Report CSV scaricato");
    } catch (error) {
      toast.error(error.message || "Errore durante l'esportazione del report");
    } finally {
      setExporting(false);
    }
  };

  const rate = conversionRate(data.conversion);
  const commerciali = useMemo(() => {
    const filter = commercialeFilter.trim().toLocaleLowerCase("it-IT");
    return data.by_commerciale
      .map((item) => ({ name: label(item._id), shortName: shortLabel(item._id), count: number(item.count) }))
      .filter((item) => !filter || item.name.toLocaleLowerCase("it-IT").includes(filter))
      .sort((a, b) => b.count - a.count);
  }, [data.by_commerciale, commercialeFilter]);
  const andamento = useMemo(
    () =>
      data.andamento.map((item) => ({
        date: label(item._id),
        shortDate: shortLabel(item._id, 12),
        count: number(item.count),
      })),
    [data.andamento]
  );
  const pipeline = useMemo(
    () =>
      data.pipeline
        .map((item) => ({ name: label(item._id), count: number(item.count) }))
        .sort((a, b) => b.count - a.count),
    [data.pipeline]
  );
  const pipelineTotal = pipeline.reduce((sum, item) => sum + item.count, 0);

  const actions = (
    <>
      {!controlled ? (
        <select
          aria-label="Periodo del report"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
          value={days}
          onChange={(event) => setDays(event.target.value)}
        >
          {PERIODS.map((period) => (
            <option key={period.value} value={period.value}>
              {period.label}
            </option>
          ))}
        </select>
      ) : null}
      <Button type="button" variant="outline" size="sm" onClick={loadReport} disabled={loading}>
        <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        Aggiorna
      </Button>
      <Button type="button" size="sm" onClick={exportCsv} disabled={exporting}>
        <Download className="mr-1.5 h-4 w-4" />
        {exporting ? "Esportazione…" : "Esporta CSV"}
      </Button>
    </>
  );

  return (
    <div className="space-y-5">
      {embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-3 border-t border-admin-border pt-5">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-admin-text">
              <BarChart3 className="h-5 w-5 text-admin-accent" aria-hidden />
              Report commerciale Affidya (lead strutture)
            </h3>
            <p className="mt-1 text-sm text-admin-muted">
              Lead strutture creati nel periodo: conversioni, distribuzione per commerciale e pipeline. Export CSV con gli stessi filtri.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </div>
      ) : (
        <AdminPageHeader
          icon={BarChart3}
          title="Report commerciale"
          description="Conversioni e pipeline dei lead strutture creati nel periodo selezionato."
          actions={actions}
        />
      )}

      {error && !loading ? <StateBlock state="error" description={error} onRetry={loadReport} /> : (
      <>
      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard icon={TrendingUp} label="Tasso di conversione" value={`${rate.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`} />
        <MetricCard icon={Users} label="Clienti convertiti" value={number(data.conversion.clienti).toLocaleString("it-IT")} />
        <MetricCard icon={BarChart3} label="Opportunità analizzate" value={number(data.conversion.total).toLocaleString("it-IT")} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader className="space-y-2 pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Lead strutture per commerciale</CardTitle>
              <span className="text-xs text-slate-500">{commerciali.length} commerciali</span>
            </div>
            <Input
              className="max-w-xs"
              value={commercialeFilter}
              onChange={(event) => setCommercialeFilter(event.target.value)}
              placeholder="Filtra per commerciale…"
              aria-label="Filtra per commerciale"
            />
          </CardHeader>
          <CardContent>
            <ChartPlaceholder show={loading} empty={!commerciali.length} emptyText="Nessun lead strutture creato nel periodo.">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={commerciali} margin={{ top: 8, right: 12, left: -18, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="shortName" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [value, "Lead"]} labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""} />
                    <Bar dataKey="count" name="Lead" fill="#166534" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartPlaceholder>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Andamento nuovi lead strutture</CardTitle>
            <p className="text-xs text-slate-500">Lead strutture creati per giorno nel periodo.</p>
          </CardHeader>
          <CardContent>
            <ChartPlaceholder show={loading} empty={!andamento.length} emptyText="Nessun dato di andamento disponibile.">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={andamento} margin={{ top: 8, right: 12, left: -18, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="shortDate" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [value, "Lead"]} labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""} />
                    <Bar dataKey="count" name="Lead" fill="#15803d" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartPlaceholder>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Riepilogo pipeline</CardTitle>
          <p className="text-xs text-slate-500">{pipelineTotal} opportunità distribuite per stato.</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-500">Caricamento dati…</p>
          ) : !pipeline.length ? (
            <p className="py-8 text-center text-sm text-slate-500">Nessun dato della pipeline disponibile.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {pipeline.map((item) => (
                <div key={item.name} className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                  <p className="truncate text-xs font-medium text-slate-500" title={item.name}>
                    {item.name}
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{item.count}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {pipelineTotal ? `${((item.count / pipelineTotal) * 100).toFixed(1)}% del totale` : "—"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label: title, value }) {
  return (
    <Card className="shadow-sm">
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
        </div>
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-admin-accent-soft text-admin-accent">
          <Icon className="h-5 w-5" />
        </span>
      </CardContent>
    </Card>
  );
}

function ChartPlaceholder({ show, empty, emptyText, children }) {
  if (show) return <p className="flex h-72 items-center justify-center text-sm text-slate-500">Caricamento dati…</p>;
  if (empty) return <p className="flex h-72 items-center justify-center text-sm text-slate-500">{emptyText}</p>;
  return children;
}
